import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customerAPI, artifactAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap, Home, User, BarChart3, Users, CreditCard, Settings, LogOut, Menu,
  Plus, Trash2, UserPlus, Package, Link2, Unlink, Calendar, ShoppingBag, ClipboardList
} from 'lucide-react';

const CustomersPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', notes: '' });
  const [assignModal, setAssignModal] = useState(null);
  const [selectedArtifact, setSelectedArtifact] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [custRes, artRes] = await Promise.all([customerAPI.list(), artifactAPI.list()]);
      setCustomers(custRes.data);
      setArtifacts(artRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Name is required'); return; }
    try {
      const res = await customerAPI.create(formData);
      setCustomers([...customers, res.data]);
      setFormData({ name: '', email: '', phone: '', company: '', notes: '' });
      setShowForm(false);
      toast.success('Customer created');
    } catch (error) {
      toast.error('Failed to create customer');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await customerAPI.delete(customerId);
      setCustomers(customers.filter(c => c.customer_id !== customerId));
      setArtifacts(artifacts.map(a => a.customer_id === customerId ? { ...a, customer_id: null } : a));
      toast.success('Customer deleted');
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleAssign = async () => {
    if (!selectedArtifact) return;
    try {
      await artifactAPI.update(selectedArtifact, { customer_id: assignModal });
      setArtifacts(artifacts.map(a => a.artifact_id === selectedArtifact ? { ...a, customer_id: assignModal } : a));
      setAssignModal(null);
      setSelectedArtifact('');
      toast.success('Artifact assigned');
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  const handleUnassign = async (artifactId) => {
    try {
      await artifactAPI.update(artifactId, { customer_id: null });
      setArtifacts(artifacts.map(a => a.artifact_id === artifactId ? { ...a, customer_id: null } : a));
      toast.success('Artifact unassigned');
    } catch (error) {
      toast.error('Failed to unassign');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers', active: true },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  const giveawayArtifacts = artifacts.filter(a => a.mode === 'giveaway');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Customers</h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-customer-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Customer
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6" data-testid="customers-page">
          {/* Create Form */}
          {showForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="customer-form">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                New Customer
              </h3>
              <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input className="mt-1" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required data-testid="customer-name-input" />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input className="mt-1" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} data-testid="customer-company-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input className="mt-1" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} data-testid="customer-email-input" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input className="mt-1" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} data-testid="customer-phone-input" />
                </div>
                <div className="sm:col-span-2 flex gap-3">
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-customer-btn">Create Customer</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </div>
          )}

          {/* Assign Modal */}
          {assignModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(null)}>
              <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()} data-testid="assign-modal">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Assign Giveaway Artifact
                </h3>
                {giveawayArtifacts.length === 0 ? (
                  <div className="text-center py-6">
                    <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No giveaway artifacts available.</p>
                    <p className="text-slate-400 text-xs mt-1">Create a giveaway artifact on the Artifacts page first.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Label>Select Giveaway Artifact</Label>
                    <Select value={selectedArtifact} onValueChange={setSelectedArtifact}>
                      <SelectTrigger data-testid="assign-artifact-select"><SelectValue placeholder="Choose artifact..." /></SelectTrigger>
                      <SelectContent>
                        {giveawayArtifacts.filter(a => !a.customer_id).map(a => (
                          <SelectItem key={a.artifact_id} value={a.artifact_id}>
                            {a.label || a.artifact_type} {a.campaign_name ? `(${a.campaign_name})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAssign} className="bg-indigo-600 hover:bg-indigo-700" disabled={!selectedArtifact} data-testid="confirm-assign-btn">Assign</Button>
                      <Button variant="outline" onClick={() => setAssignModal(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customer List */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Customers ({customers.length})
              </h3>
            </div>

            {customers.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No customers yet</p>
                <p className="text-sm text-slate-400">Create customers and assign giveaway artifacts to them</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {customers.map(customer => {
                  const assigned = artifacts.filter(a => a.customer_id === customer.customer_id);
                  return (
                    <div key={customer.customer_id} className="px-6 py-4 hover:bg-slate-50" data-testid={`customer-${customer.customer_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-600 font-semibold text-sm">{customer.name?.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{customer.name}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              {customer.company && <span>{customer.company}</span>}
                              {customer.email && <span>{customer.email}</span>}
                              {customer.phone && <span>{customer.phone}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => setAssignModal(customer.customer_id)} data-testid={`assign-to-${customer.customer_id}`}>
                            <Link2 className="w-4 h-4 mr-1" /> Assign Artifact
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(customer.customer_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-customer-${customer.customer_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {assigned.length > 0 && (
                        <div className="mt-3 ml-14">
                          <p className="text-xs text-slate-500 font-medium mb-2">Assigned Artifacts ({assigned.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {assigned.map(a => (
                              <div key={a.artifact_id} className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 rounded-lg text-sm">
                                <Package className="w-3.5 h-3.5 text-indigo-600" />
                                <span className="text-indigo-700 font-medium">{a.label || a.artifact_type}</span>
                                {a.campaign_name && <span className="text-indigo-400 text-xs">({a.campaign_name})</span>}
                                <button onClick={() => handleUnassign(a.artifact_id)} className="text-slate-400 hover:text-red-500 ml-1" data-testid={`unassign-${a.artifact_id}`}>
                                  <Unlink className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomersPage;
